import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormGroup
} from '@mui/material';
import { useApi } from '@bumble/app/hooks/use-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';

interface EthernetDeviceOamCcmInfoEditProps {
  onSubmit: () => void;
  metroID: string;
  loopbackEnabled: boolean;
  ccpEnabled: boolean;
  ccmInterval: string;
  mdName: string;
  mdLevel: string;
  mdAssociation: string;
  mepID: string;
  oamVlanID: string;
};

export const EthernetDeviceOamCcmInfoEdit = ({
  onSubmit, metroID, loopbackEnabled, ccpEnabled, ccmInterval,
  mdName, mdLevel, mdAssociation, mepID, oamVlanID
}: EthernetDeviceOamCcmInfoEditProps) => {
  const api = useApi();
  const [newLoopbackEnabled, setNewLoopbackEnabled] = useState<boolean>(loopbackEnabled);
  const [newCcpEnabled, setNewCcpEnabled] = useState<boolean>(ccpEnabled);
  const [newCcmInterval, setNewCcmInterval] = useState<string>(ccmInterval);
  const [newMdName, setNewMdName] = useState<string>(mdName);
  const [newMdLevel, setNewMdLevel] = useState<string>(mdLevel);
  const [newMdAssociation, setNewMdAssociation] = useState<string>(mdAssociation);
  const [newMepID, setNewMepID] = useState<string>(mepID);
  const [newOamVlanID, setNewOamVlanID] = useState<string>(oamVlanID);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [intervalItems, setIntervalItems] = useState<number[]>([]);

  const metroOamCcmInfoUpdatePost = useMutation(api.editOamApiV1EditOamPost, {
    onSuccess() {
      toast.success('Updated Metro OAM info successfully.');
      onSubmit();
    },
    onError: (error) => {
      setDisableSubmit(false);
      apiError(error);
    },
  });

  const {
    data: intervalData,
    isLoading
  } = useQuery(
    ['intervalData'],
    api.metroCcmIntervalApiV1MetroCcmIntervalGet.bind(null, {}),
    { enabled: true },
  );

  useEffect(() => {
    if (intervalData?.data && Array.isArray(intervalData?.data)) {
      setIntervalItems(intervalData?.data);
      setNewCcmInterval(ccmInterval ?? (intervalData?.data.length ? intervalData?.data[0]?.toString() : ''));
    }
  }, [intervalData]);

  const isDisabled = () => {
    return newCcpEnabled && (
      isNaN(Number(newCcmInterval))
      || !newMdName
      || !newMdLevel || isNaN(Number(newMdLevel))
      || !newMdAssociation
      || !newMepID || isNaN(Number(newMepID))
      || !newOamVlanID || isNaN(Number(newOamVlanID))
    );
  };

  const handleSubmit = () => {
    setDisableSubmit(true);
    metroOamCcmInfoUpdatePost.mutate({
      metro_id: metroID,
      loopback_enabled: newLoopbackEnabled,
      ccp_enabled: newCcpEnabled,
      ...(newCcpEnabled && {
        ccm_interval: Number(newCcmInterval),
        md_name: newMdName,
        md_level: Number(newMdLevel),
        md_association: newMdAssociation,
        mep_id: Number(newMepID),
        vlan_id: Number(newOamVlanID),
      })
    });
  };

  return (
    <Box pt={2}>
      {isLoading ? <Loading /> :
        <>
          <FormGroup>
            <FormControlLabel
              sx={{ mb: 4 }}
              control={
                <Checkbox
                  checked={newLoopbackEnabled}
                  onChange={(e) => setNewLoopbackEnabled(e.target.checked)}
                />
              }
              label="Reply to Loopback messages"
            />
          </FormGroup>
          <FormGroup>
            <FormControlLabel
              sx={{ mb: 4 }}
              control={
                <Checkbox
                  checked={newCcpEnabled}
                  onChange={(e) => setNewCcpEnabled(e.target.checked)}
                />
              }
              label="Continuity Check Protocol"
            />
          </FormGroup>
          {newCcpEnabled && (<>
            <FormControl fullWidth sx={{ mb: 4 }}>
              <InputLabel id="interval-period-label">Continuity check message interval</InputLabel>
              <Select
                labelId="interval-period-label"
                id="interval-period"
                label="Continuity check message interval"
                value={newCcmInterval}
                onChange={(e) => setNewCcmInterval(e.target.value)}
              >
                {intervalItems.map((item: number, idx) => (
                  <MenuItem value={item.toString()} key={idx}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="Maintenance domain name"
              variant="outlined"
              value={newMdName}
              onChange={(e) => setNewMdName(e.target.value)}
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="Maintenance domain level"
              variant="outlined"
              value={newMdLevel}
              onChange={(e) => setNewMdLevel(e.target.value)}
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="Maintenance domain association"
              variant="outlined"
              value={newMdAssociation}
              onChange={(e) => setNewMdAssociation(e.target.value)}
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="Maintenance endpoint ID"
              variant="outlined"
              value={newMepID}
              onChange={(e) => setNewMepID(e.target.value)}
            />
            <TextField
              sx={{ mb: 4 }}
              fullWidth
              label="VLAN ID"
              variant="outlined"
              value={newOamVlanID}
              onChange={(e) => setNewOamVlanID(e.target.value)}
            />
          </>)}
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
            <Button variant="outlined" onClick={handleSubmit} disabled={disableSubmit}>
              Save
            </Button>
          </Box>
        </>}
    </Box>
  );
};